/**
 * Lightweight calendar math used by ScheduleCalendar.
 *
 * Everything operates on `Date` objects with hours zeroed to local
 * midnight (so date-arithmetic stays predictable across DST). Helpers
 * avoid `toISOString()` because that drifts by a day in any timezone
 * ahead of UTC — see lib/dateUtils.js for the same rationale.
 */
import { isoDate } from './dateUtils';

export const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export const startOfWeek = (d) => {
  const x = startOfDay(d);
  // Monday-first to match UK ops convention used everywhere else.
  const diff = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
};

export const startOfMonth = (d) => {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
};

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export const addMonths = (d, n) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
};

export const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/** All 7 dates of the week containing `d`, Monday first. */
export const weekDays = (d) => {
  const start = startOfWeek(d);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

/** 6×7 grid of dates that contains the month of `d` (Mon-first, with
 *  leading/trailing days from neighbouring months for full weeks). */
export const monthGrid = (d) => {
  const first = startOfMonth(d);
  const start = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

export const rangeForView = (view, ref) => {
  if (view === 'day') {
    const d = startOfDay(ref);
    return { from: d, to: d, label: ref.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) };
  }
  if (view === 'week') {
    const from = startOfWeek(ref);
    const to = addDays(from, 6);
    return {
      from, to,
      label: `${from.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${to.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`,
    };
  }
  // month
  const from = startOfMonth(ref);
  const to = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  return {
    from, to,
    label: ref.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
  };
};

export const isoOf = isoDate;

/** Compare 'HH:MM' strings — used for sorting same-day shifts. */
export const cmpTime = (a, b) => (a || '').localeCompare(b || '');

/** Convert "HH:MM" → fractional hours (e.g. "08:30" → 8.5). */
export const hhmmToHours = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) + ((m || 0) / 60);
};
